import { useState, useEffect, useCallback, useRef } from "react";
import API_BASE_URL from "../config";

// ─── Auto-generate number ─────────────────────────────────────────────────────
const generateNumber = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
};

const today = new Date().toISOString().split("T")[0];

const getInitialForm = () => ({
  number: generateNumber(),
  series: "Primary",
  status: "Open",
  customer: "",
  name: "",
  contactPerson: "",
  customerRefNo: "",
  localCurrency: "PHP",
  postingDate: today,
  deliveryDate: "",
  documentDate: today,
  salesEmployee: "No Sales Employee",
  owner: "",
  remarks: "",
  requestor: "",
  department: "",
  billOfLading: "",
  discount: "",
  rounding: false,
  tax: "",
  items: [
    { itemNo: "", itemDescription: "", quantity: "", uom: "", whse: "Whse 2nd Floor", addtlInfo: "", inStock: "", amount: "", totalLC: "", unitPrice: "", department: "", itemDesc2: "" },
  ],
});

const computeTotals = (items, discount, tax) => {
  const totalBefore = items.reduce((sum, i) => sum + (Number(i.unitPrice) || 0) * (Number(i.quantity) || 0), 0);
  const total = totalBefore - (Number(discount) || 0) + (Number(tax) || 0);
  return { totalBefore, total };
};

// ─── Search Icon ──────────────────────────────────────────────────────────────
const SearchIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// ─── Requestor Dropdown ───────────────────────────────────────────────────────
function RequestorDropdown({ value, department, onSelect }) {
  const [requestors, setRequestors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const wrapRef = useRef(null);

  // Fetch once on mount
  useEffect(() => {
    const fetch_ = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}api/Requestor`);
        if (res.ok) setRequestors(await res.json());
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch_();
  }, []);

  // Sync search input when value changes externally
  useEffect(() => { setSearch(value || ""); }, [value]);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false); setCursor(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = search.trim().length === 0
    ? requestors.slice(0, 60)
    : requestors.filter(r =>
        r.name?.toLowerCase().includes(search.toLowerCase()) ||
        r.department?.toLowerCase().includes(search.toLowerCase())
      ).slice(0, 60);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") { setOpen(true); setCursor(0); e.preventDefault(); }
      return;
    }
    if (e.key === "ArrowDown") { setCursor(c => Math.min(c + 1, filtered.length - 1)); e.preventDefault(); }
    else if (e.key === "ArrowUp") { setCursor(c => Math.max(c - 1, 0)); e.preventDefault(); }
    else if (e.key === "Enter" && cursor >= 0 && filtered[cursor]) {
      handleSelect(filtered[cursor]); e.preventDefault();
    } else if (e.key === "Escape") { setOpen(false); setCursor(-1); }
  };

  const handleSelect = (r) => {
    setSearch(r.name);
    setOpen(false);
    setCursor(-1);
    onSelect(r);
  };

  const getDeptBadgeColor = (dept) => {
    const map = {
      "QC": "bg-blue-50 text-blue-700 border-blue-200",
      "General Services": "bg-green-50 text-green-700 border-green-200",
      "Marketing": "bg-purple-50 text-purple-700 border-purple-200",
      "Finance": "bg-yellow-50 text-yellow-700 border-yellow-200",
      "IT": "bg-cyan-50 text-cyan-700 border-cyan-200",
      "HR": "bg-pink-50 text-pink-700 border-pink-200",
      "Operations": "bg-orange-50 text-orange-700 border-orange-200",
    };
    return map[dept] || "bg-gray-100 text-gray-600 border-gray-200";
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      {/* Input */}
      <div className="relative">
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true); setCursor(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={loading ? "Loading requestors…" : "Search requestor…"}
          autoComplete="off"
          className="w-full border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-yellow-50"
          style={{ paddingRight: "2rem" }}
        />
        {loading
          ? <svg className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
          : <SearchIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300" />
        }
      </div>

      {/* Dropdown */}
      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[999] overflow-y-auto"
          style={{ minWidth: 320, maxWidth: 420, maxHeight: 240, fontSize: ".82rem" }}>
          <div className="px-3 py-1 bg-gray-50 border-b border-gray-100 text-[10px] text-gray-400 font-semibold rounded-t-xl">
            {filtered.length} result{filtered.length !== 1 ? "s" : ""} &nbsp;·&nbsp; ↑↓ navigate &nbsp;·&nbsp; Enter select
          </div>
          {filtered.map((r, i) => (
            <div
              key={r.id}
              onMouseDown={() => handleSelect(r)}
              onMouseEnter={() => setCursor(i)}
              className={`px-3 py-2 cursor-pointer border-b border-gray-50 last:border-0 transition-colors ${cursor === i ? "bg-blue-50" : "hover:bg-blue-50"}`}
            >
              <div className="flex items-center gap-2">
                {/* Avatar */}
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-700 font-bold text-[9px]">
                    {r.name?.split(" ").map(n => n[0]).slice(0, 2).join("")}
                  </span>
                </div>
                <span className="font-semibold text-gray-800 flex-1 text-xs">{r.name}</span>
                {r.department && (
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${getDeptBadgeColor(r.department)}`}>
                    {r.department}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results */}
      {open && search.trim().length > 0 && filtered.length === 0 && !loading && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[999] px-4 py-3 text-xs text-gray-400"
          style={{ minWidth: 280 }}>
          No requestors found for "{search}"
        </div>
      )}
    </div>
  );
}

// ─── Generic Typeahead Input ──────────────────────────────────────────────────
function TypeaheadInput({ value, onChange, onSelect, onOpenModal, items: allItems, filterFn, renderRow, placeholder, inputClassName, wrapClassName }) {
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const wrapRef = useRef(null);

  const filtered = value.trim().length === 0
    ? allItems.slice(0, 50)
    : allItems.filter(i => filterFn(i, value)).slice(0, 50);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false); setCursor(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") { setOpen(true); setCursor(0); e.preventDefault(); }
      return;
    }
    if (e.key === "ArrowDown") { setCursor(c => Math.min(c + 1, filtered.length - 1)); e.preventDefault(); }
    else if (e.key === "ArrowUp") { setCursor(c => Math.max(c - 1, 0)); e.preventDefault(); }
    else if (e.key === "Enter" && cursor >= 0 && filtered[cursor]) {
      onSelect(filtered[cursor]); setOpen(false); setCursor(-1); e.preventDefault();
    } else if (e.key === "Escape") { setOpen(false); setCursor(-1); }
  };

  return (
    <div ref={wrapRef} className={`relative ${wrapClassName || ""}`}>
      <input
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); setCursor(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={inputClassName}
        autoComplete="off"
        style={{ paddingRight: "1.75rem" }}
      />
      <button
        type="button"
        onMouseDown={e => { e.preventDefault(); e.stopPropagation(); setOpen(false); onOpenModal && onOpenModal(); }}
        title="Browse"
        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-300 hover:text-blue-600 transition-colors"
      >
        <SearchIcon />
      </button>
      {open && filtered.length > 0 && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-xl z-[999] overflow-y-auto"
          style={{ minWidth: 280, maxWidth: 400, maxHeight: 220, fontSize: ".82rem" }}>
          <div className="px-3 py-1 bg-gray-50 border-b border-gray-100 text-[10px] text-gray-400 font-semibold rounded-t-xl">
            {filtered.length} result{filtered.length !== 1 ? "s" : ""} &nbsp;·&nbsp; ↑↓ navigate &nbsp;·&nbsp; Enter select
          </div>
          {filtered.map((item, i) => (
            <div
              key={item.id || item.itemCode || item.bpCode || i}
              onMouseDown={() => { onSelect(item); setOpen(false); setCursor(-1); }}
              onMouseEnter={() => setCursor(i)}
              className={`px-3 py-2 cursor-pointer border-b border-gray-50 last:border-0 transition-colors ${cursor === i ? "bg-blue-50" : "hover:bg-blue-50"}`}
            >
              {renderRow(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Customer Lookup Modal ────────────────────────────────────────────────────
function CustomerModal({ open, onClose, onSelect }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const fetchCustomers = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}api/Customer_marketing`);
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      setCustomers(await res.json());
    } catch (err) { setError(err.message || "Failed to load customers."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { setSearch(""); fetchCustomers(); } }, [open, fetchCustomers]);

  const filtered = customers.filter(c =>
    c.bpCode?.toLowerCase().includes(search.toLowerCase()) ||
    c.bpName?.toLowerCase().includes(search.toLowerCase())
  );

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-2xl mx-4 flex flex-col max-h-[75vh] z-10 overflow-hidden">
        <div className="bg-blue-700 px-5 py-3.5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <SearchIcon className="w-4 h-4 text-blue-200" />
            <h2 className="text-white font-bold text-sm">Customer Lookup</h2>
            <span className="text-xs bg-blue-600 text-blue-100 border border-blue-500 px-2.5 py-0.5 rounded-full">Marketing</span>
          </div>
          <button onClick={onClose} className="text-blue-200 hover:text-white rounded-lg p-1 hover:bg-blue-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search BP code or name…"
              className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50" />
          </div>
          <p className="text-[10px] text-gray-400 mt-1.5">{filtered.length} customer{filtered.length !== 1 ? "s" : ""} — click a row to select</p>
        </div>
        <div className="overflow-y-auto flex-1">
          {error && <div className="mx-4 mt-3 bg-red-50 border border-red-100 text-red-600 px-4 py-2.5 rounded-lg text-xs">{error} <button onClick={fetchCustomers} className="ml-2 underline font-semibold">Retry</button></div>}
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400 gap-2.5">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
              <span className="text-sm">Loading customers…</span>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0">
                <tr className="bg-gray-50 border-b border-gray-200">
                  {["BP Code", "BP Name", "Group Code", "Sales Employee"].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-12 text-gray-400 text-sm">No customers match your search.</td></tr>
                ) : filtered.map(c => (
                  <tr key={c.id} onClick={() => onSelect(c)} className="hover:bg-blue-50 cursor-pointer transition-colors group">
                    <td className="px-4 py-2.5 font-mono text-xs font-semibold text-blue-700">{c.bpCode}</td>
                    <td className="px-4 py-2.5 text-gray-700 text-xs max-w-[220px] truncate">{c.bpName || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{c.groupCode || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{c.salesEmployeeCode || <span className="text-gray-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="border-t border-gray-100 px-4 py-2.5 flex items-center bg-gray-50 flex-shrink-0">
          <button onClick={onClose} className="ml-auto px-4 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-100">Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Inventory Lookup Modal ───────────────────────────────────────────────────
function InventoryModal({ open, onClose, onSelect }) {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const fetchInventory = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}api/Marketing_items`);
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      setInventory(await res.json());
    } catch (err) { setError(err.message || "Failed to load inventory."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) { setSearch(""); fetchInventory(); } }, [open, fetchInventory]);

  const filtered = inventory.filter(item =>
    item.itemCode?.toLowerCase().includes(search.toLowerCase()) ||
    item.description?.toLowerCase().includes(search.toLowerCase()) ||
    item.category?.toLowerCase().includes(search.toLowerCase())
  );

  const getStockBadge = (avail) => {
    if (avail <= 0) return <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-50 text-red-600 border border-red-100">Out of stock</span>;
    if (avail < 10) return <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-yellow-50 text-yellow-700 border border-yellow-100">Low stock</span>;
    return <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-700 border border-green-100">In stock</span>;
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-3xl mx-4 flex flex-col max-h-[80vh] z-10 overflow-hidden">
        <div className="bg-blue-700 px-5 py-3.5 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <SearchIcon className="w-4 h-4 text-blue-200" />
            <h2 className="text-white font-bold text-sm">Inventory Lookup</h2>
            <span className="text-xs bg-blue-600 text-blue-100 border border-blue-500 px-2.5 py-0.5 rounded-full">Marketing</span>
          </div>
          <button onClick={onClose} className="text-blue-200 hover:text-white rounded-lg p-1 hover:bg-blue-600">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search item no, description, category…"
              className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50" />
          </div>
          <p className="text-[10px] text-gray-400 mt-1.5">{filtered.length} item{filtered.length !== 1 ? "s" : ""} — click a row to select</p>
        </div>
        <div className="overflow-y-auto flex-1">
          {error && <div className="mx-4 mt-3 bg-red-50 border border-red-100 text-red-600 px-4 py-2.5 rounded-lg text-xs">{error} <button onClick={fetchInventory} className="ml-2 underline font-semibold">Retry</button></div>}
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400 gap-2.5">
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
              <span className="text-sm">Loading inventory…</span>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0">
                <tr className="bg-gray-50 border-b border-gray-200">
                  {["Item Code", "Description", "Unit", "Category", "Available Qty", "Qty", "Status"].map(h => (
                    <th key={h} className="text-left px-4 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">No items match your search.</td></tr>
                ) : filtered.map(item => (
                  <tr key={item.id} onClick={() => onSelect(item)} className="hover:bg-blue-50 cursor-pointer transition-colors">
                    <td className="px-4 py-2.5 font-mono text-xs font-semibold text-blue-700">{item.itemCode}</td>
                    <td className="px-4 py-2.5 text-gray-700 text-xs max-w-[200px] truncate">{item.description || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2.5">
                      {item.unit ? <span className="bg-gray-100 text-gray-600 text-[10px] font-mono font-semibold px-2 py-0.5 rounded border border-gray-200">{item.unit}</span> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">{item.category || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-2.5 tabular-nums text-xs">
                      <span className={`font-bold ${item.availableQty > 0 ? "text-green-600" : "text-red-500"}`}>{Number(item.availableQty || 0).toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-2.5 tabular-nums text-xs font-medium text-gray-700">{Number(item.qty || 0).toLocaleString()}</td>
                    <td className="px-4 py-2.5">{getStockBadge(item.availableQty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="border-t border-gray-100 px-4 py-2.5 flex items-center gap-4 bg-gray-50 flex-shrink-0">
          <span className="flex items-center gap-1.5 text-[10px] text-gray-400"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" /> Available = ready to use</span>
          <span className="flex items-center gap-1.5 text-[10px] text-gray-400"><span className="w-2 h-2 rounded-full bg-orange-400 inline-block" /> Reserved = held by SO</span>
          <button onClick={onClose} className="ml-auto px-4 py-1.5 border border-gray-200 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-100">Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Item Row ─────────────────────────────────────────────────────────────────
function ItemRow({ item, idx, totalItems, onItemChange, onRemove, onOpenLookup, allInventory }) {
  const handleField = (field, value) => onItemChange(idx, field, value);

  const invFilterFn = (i, val) =>
    i.itemCode?.toLowerCase().includes(val.toLowerCase()) ||
    i.description?.toLowerCase().includes(val.toLowerCase());

  const handleSelectInv = (selected) => {
    onItemChange(idx, "__select__", selected);
  };

  const inputCls = "w-full border border-gray-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";

  return (
    <tr className="hover:bg-blue-50/30 transition-colors">
      <td className="px-3 py-2 text-gray-400 font-mono text-xs">{idx + 1}</td>
      <td className="px-2 py-2 min-w-[140px]" style={{ overflow: "visible", position: "relative", zIndex: 20 - idx }}>
        <TypeaheadInput
          value={item.itemNo}
          onChange={val => handleField("itemNo", val)}
          onSelect={handleSelectInv}
          onOpenModal={() => onOpenLookup(idx)}
          items={allInventory}
          filterFn={invFilterFn}
          renderRow={i => (
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-blue-700 min-w-[70px]">{i.itemCode}</span>
              <span className="text-gray-600 text-xs flex-1 truncate">{i.description}</span>
              <span className={`text-xs font-bold ${(i.availableQty || 0) > 0 ? "text-green-600" : "text-red-500"}`}>{Number(i.availableQty || 0).toLocaleString()}</span>
            </div>
          )}
          placeholder="Item No."
          inputClassName={inputCls}
          wrapClassName="w-full"
        />
      </td>
      <td className="px-2 py-2 min-w-[190px]" style={{ overflow: "visible", position: "relative", zIndex: 19 - idx }}>
        <TypeaheadInput
          value={item.itemDescription}
          onChange={val => handleField("itemDescription", val)}
          onSelect={handleSelectInv}
          onOpenModal={() => onOpenLookup(idx)}
          items={allInventory}
          filterFn={invFilterFn}
          renderRow={i => (
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs font-bold text-blue-700 min-w-[70px]">{i.itemCode}</span>
              <span className="text-gray-600 text-xs flex-1 truncate">{i.description}</span>
            </div>
          )}
          placeholder="Description"
          inputClassName={inputCls}
          wrapClassName="w-full"
        />
      </td>
      <td className="px-2 py-2 min-w-[80px]">
        <input type="number" value={item.quantity} onChange={e => handleField("quantity", e.target.value)} placeholder="0" className={inputCls} />
      </td>
      <td className="px-2 py-2 min-w-[80px]">
        <input value={item.uom} onChange={e => handleField("uom", e.target.value)} placeholder="PCS" className={inputCls} />
      </td>
      <td className="px-2 py-2 min-w-[90px]">
        <input value={item.whse} onChange={e => handleField("whse", e.target.value)} placeholder="Whse 2nd Floor" className={inputCls} />
      </td>
      <td className="px-2 py-2 min-w-[120px]">
        <input value={item.addtlInfo} onChange={e => handleField("addtlInfo", e.target.value)} placeholder="—" className={inputCls} />
      </td>
      <td className="px-2 py-2 min-w-[90px]">
        <input type="number" value={item.inStock} onChange={e => handleField("inStock", e.target.value)} placeholder="0" className={`${inputCls} bg-gray-50`} />
      </td>
      <td className="px-2 py-2 min-w-[100px]">
        <input value={item.amount} readOnly className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`} />
      </td>
      <td className="px-2 py-2 min-w-[100px]">
        <input value={item.totalLC} readOnly className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`} />
      </td>
      <td className="px-2 py-2 min-w-[100px]">
        <input type="number" value={item.unitPrice} onChange={e => handleField("unitPrice", e.target.value)} placeholder="0.000000" className={inputCls} />
      </td>
      <td className="px-2 py-2 min-w-[100px]">
        <input value={item.department} onChange={e => handleField("department", e.target.value)} placeholder="—" className={inputCls} />
      </td>
      <td className="px-2 py-2 min-w-[120px]">
        <input value={item.itemDesc2} onChange={e => handleField("itemDesc2", e.target.value)} placeholder="—" className={inputCls} />
      </td>
      <td className="px-2 py-2">
        {totalItems > 1 && (
          <button onClick={onRemove} className="text-gray-300 hover:text-red-500 transition">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </td>
    </tr>
  );
}

// ─── Main Form ────────────────────────────────────────────────────────────────
export default function SO_Marketing_FORM() {
  const [form, setForm] = useState(getInitialForm());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [savedData, setSavedData] = useState(null);
  const [activeTab, setActiveTab] = useState("contents");

  const [allInventory, setAllInventory] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [custLoading, setCustLoading] = useState(false);

  const [invModalOpen, setInvModalOpen] = useState(false);
  const [custModalOpen, setCustModalOpen] = useState(false);
  const [activeRowIdx, setActiveRowIdx] = useState(null);

  useEffect(() => {
    const fetchInv = async () => {
      setInvLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}api/Marketing_items`);
        if (res.ok) { const d = await res.json(); setAllInventory(d.filter(i => i.isActive === 1)); }
      } catch (e) { console.error(e); }
      setInvLoading(false);
    };
    const fetchCust = async () => {
      setCustLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}api/Customer_marketing`);
        if (res.ok) setAllCustomers(await res.json());
      } catch (e) { console.error(e); }
      setCustLoading(false);
    };
    fetchInv();
    fetchCust();
  }, []);

  const handleField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // ── Requestor select — auto-fills Department ──────────────────────────────
  const handleSelectRequestor = (r) => {
    setForm(f => ({
      ...f,
      requestor: r.name || "",
      department: r.department || f.department,
    }));
  };

  const handleItem = (idx, field, value) => {
    const items = [...form.items];
    if (field === "__select__") {
      const sel = value;
      items[idx] = {
        ...items[idx],
        itemNo: sel.itemCode || "",
        itemDescription: sel.description || "",
        uom: sel.unit || items[idx].uom,
        inStock: sel.availableQty ?? sel.qty ?? items[idx].inStock,
      };
    } else {
      items[idx] = { ...items[idx], [field]: value };
      if (field === "unitPrice" || field === "quantity") {
        const qty = Number(field === "quantity" ? value : items[idx].quantity) || 0;
        const price = Number(field === "unitPrice" ? value : items[idx].unitPrice) || 0;
        items[idx].amount = (qty * price).toFixed(4);
        items[idx].totalLC = items[idx].amount;
      }
    }
    setForm(f => ({ ...f, items }));
  };

  const addItem = () =>
    setForm(f => ({
      ...f,
      items: [...f.items, { itemNo: "", itemDescription: "", quantity: "", uom: "", whse: "Whse 2nd Floor", addtlInfo: "", inStock: "", amount: "", totalLC: "", unitPrice: "", department: "", itemDesc2: "" }],
    }));

  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const handleOpenInvLookup = (idx) => { setActiveRowIdx(idx); setInvModalOpen(true); };
  const handleSelectInventory = (inventoryItem) => {
    if (activeRowIdx === null) return;
    handleItem(activeRowIdx, "__select__", inventoryItem);
    setInvModalOpen(false);
    setActiveRowIdx(null);
  };

  const handleSelectCustomer = (c) => {
    setForm(f => ({ ...f, customer: c.bpCode || "", name: c.bpName || "" }));
    setCustModalOpen(false);
  };

  const handleSelectCustomerTypeahead = (c) => {
    setForm(f => ({ ...f, customer: c.bpCode || "", name: c.bpName || "" }));
  };

  const { totalBefore, total } = computeTotals(form.items, form.discount, form.tax);

  const handleSubmit = async () => {
    setLoading(true); setError(null);
    try {
      const payload = {
        number: form.number,
        series: form.series,
        status: form.status,
        customer: form.customer || null,
        name: form.name || null,
        contactPerson: form.contactPerson || null,
        customerRefNo: form.customerRefNo || null,
        localCurrency: form.localCurrency,
        postingDate: form.postingDate,
        deliveryDate: form.deliveryDate || null,
        documentDate: form.documentDate,
        salesEmployee: form.salesEmployee || null,
        owner: form.owner || null,
        remarks: form.remarks || null,
        requestor: form.requestor || null,
        department: form.department || null,
        billOfLading: form.billOfLading || null,
        discount: Number(form.discount) || 0,
        rounding: form.rounding,
        tax: Number(form.tax) || 0,
        totalBeforeDiscount: totalBefore,
        total: total,
        items: form.items.map(item => ({
          itemNo: item.itemNo,
          itemDescription: item.itemDescription || null,
          quantity: Number(item.quantity) || 0,
          uom: item.uom || null,
          whse: item.whse || null,
          addtlInfo: item.addtlInfo || null,
          inStock: Number(item.inStock) || 0,
          amount: Number(item.amount) || 0,
          totalLC: Number(item.totalLC) || 0,
          unitPrice: Number(item.unitPrice) || 0,
          department: item.department || null,
          itemDesc2: item.itemDesc2 || null,
        })),
      };

      const res = await fetch(`${API_BASE_URL}api/SO_Marketing`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "*/*" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const details = err?.errors ? Object.entries(err.errors).map(([k, v]) => `${k}: ${v}`).join(" | ") : null;
        throw new Error(details || err?.message || `Server error ${res.status}`);
      }

      const result = await res.json();
      setSavedData(result);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(getInitialForm());
    setSubmitted(false);
    setSavedData(null);
    setError(null);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center max-w-md w-full">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Sales Order Saved!</h2>
          <p className="text-gray-500 mb-1">SO Number: <span className="font-semibold text-blue-700">{savedData?.number || form.number}</span></p>
          <p className="text-gray-400 text-sm mb-1">ID: <span className="font-mono text-gray-600">#{savedData?.id}</span></p>
          <p className="text-gray-400 text-sm mb-6">Sales Order submitted successfully.</p>
          <button onClick={handleReset} className="bg-blue-700 text-white px-6 py-2.5 rounded-lg hover:bg-blue-800 transition font-semibold">
            New Form
          </button>
        </div>
      </div>
    );
  }

  const custFilterFn = (c, val) =>
    c.bpCode?.toLowerCase().includes(val.toLowerCase()) ||
    c.bpName?.toLowerCase().includes(val.toLowerCase());

  const inputBaseCls = "border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-yellow-50";

  return (
    <>
      <CustomerModal open={custModalOpen} onClose={() => setCustModalOpen(false)} onSelect={handleSelectCustomer} />
      <InventoryModal open={invModalOpen} onClose={() => { setInvModalOpen(false); setActiveRowIdx(null); }} onSelect={handleSelectInventory} />

      <div className="min-h-screen bg-gray-100 font-sans">
        <div className="max-w-8xl mx-auto px-6 py-8 space-y-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-visible">

            {/* Title Bar */}
            <div className="bg-blue-700 px-6 py-3 flex items-center justify-between rounded-t-2xl">
              <h1 className="text-white font-bold text-lg tracking-tight">Sales Order</h1>
              <div className="flex items-center gap-3">
                {(invLoading || custLoading) && (
                  <span className="flex items-center gap-1.5 text-blue-200 text-xs">
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
                    Loading data…
                  </span>
                )}
                <span className="text-blue-200 text-xs font-semibold">Marketing</span>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="mx-6 mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-3">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span>{error}</span>
                <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">✕</button>
              </div>
            )}

            {/* Header Fields */}
            <div className="p-6">
              <div className="flex gap-8">
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-semibold text-gray-500 w-36 uppercase tracking-wide">Customer</label>
                    <TypeaheadInput
                      value={form.customer}
                      onChange={val => handleField("customer", val)}
                      onSelect={handleSelectCustomerTypeahead}
                      onOpenModal={() => setCustModalOpen(true)}
                      items={allCustomers}
                      filterFn={custFilterFn}
                      renderRow={c => (
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-blue-700 min-w-[80px]">{c.bpCode}</span>
                          <span className="text-gray-600 text-xs truncate flex-1">{c.bpName}</span>
                        </div>
                      )}
                      placeholder="Select customer…"
                      inputClassName={`${inputBaseCls} w-full`}
                      wrapClassName="flex-1 max-w-xs"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-semibold text-gray-500 w-36 uppercase tracking-wide">Name</label>
                    <input value={form.name} onChange={e => handleField("name", e.target.value)}
                      placeholder="Auto-filled from customer"
                      className={`${inputBaseCls} flex-1 max-w-xs`} />
                  </div>
                  {[
                    { label: "Contact Person", field: "contactPerson" },
                    { label: "Customer Ref. No.", field: "customerRefNo" },
                  ].map(({ label, field }) => (
                    <div key={field} className="flex items-center gap-3">
                      <label className="text-xs font-semibold text-gray-500 w-36 uppercase tracking-wide">{label}</label>
                      <input value={form[field]} onChange={e => handleField(field, e.target.value)} className={`${inputBaseCls} flex-1 max-w-xs`} />
                    </div>
                  ))}
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-semibold text-gray-500 w-36 uppercase tracking-wide">Local Currency</label>
                    <select value={form.localCurrency} onChange={e => handleField("localCurrency", e.target.value)}
                      className="border border-gray-300 rounded px-3 py-1.5 text-sm w-24 focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option>PHP</option>
                      <option>USD</option>
                    </select>
                  </div>
                </div>

                {/* Right meta */}
                <div className="min-w-[260px] space-y-1.5 text-sm border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">No.</span>
                    <div className="flex items-center gap-2">
                      <select value={form.series} onChange={e => handleField("series", e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500">
                        <option>Primary</option>
                        <option>Secondary</option>
                      </select>
                      <input value={form.number} readOnly
                        className="border border-gray-200 rounded px-2 py-1 text-xs w-36 bg-gray-100 text-gray-500 font-mono cursor-not-allowed" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</span>
                    <select value={form.status} onChange={e => handleField("status", e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option>Open</option>
                      <option>Closed</option>
                      <option>Cancelled</option>
                    </select>
                  </div>
                  {[
                    { label: "Posting Date", field: "postingDate" },
                    { label: "Delivery Date", field: "deliveryDate" },
                    { label: "Document Date", field: "documentDate" },
                  ].map(({ label, field }) => (
                    <div key={field} className="flex items-center justify-between gap-4">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
                      <input type="date" value={form[field]} onChange={e => handleField(field, e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-t border-gray-200">
              <div className="flex">
                {["contents", "logistics", "accounting", "attachments"].map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)}
                    className={`px-6 py-2.5 text-sm font-semibold capitalize transition border-b-2 ${activeTab === tab ? "border-blue-700 text-blue-700 bg-blue-50" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                    {tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Contents Tab */}
            {activeTab === "contents" && (
              <div>
                <div className="px-4 py-2 border-b border-gray-100 flex items-center gap-3">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Item/Service Type</label>
                  <select className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option>Item</option>
                    <option>Service</option>
                  </select>
                  <div className="ml-auto flex items-center gap-2">
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Summary Type</label>
                    <select className="border border-gray-300 rounded px-3 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option>No Summary</option>
                      <option>By Items</option>
                    </select>
                  </div>
                </div>
                <div className="overflow-x-auto" style={{ overflowY: "visible" }}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        {["#", "(01) Item No.", "(02) Item Description", "(04) Qty", "(03) UOM", "(05) Whse", "(06) Addtl Info", "(07) In Stock", "Amount", "Total (LC)", "Unit Price", "Department", "Item Desc2", ""].map(h => (
                          <th key={h} className="text-left px-3 py-2.5 text-xs font-bold text-gray-500 uppercase tracking-wide whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {form.items.map((item, idx) => (
                        <ItemRow
                          key={idx}
                          item={item}
                          idx={idx}
                          totalItems={form.items.length}
                          onItemChange={handleItem}
                          onRemove={() => removeItem(idx)}
                          onOpenLookup={handleOpenInvLookup}
                          allInventory={allInventory}
                        />
                      ))}
                      {Array.from({ length: Math.max(0, 4 - form.items.length) }).map((_, i) => (
                        <tr key={`empty-${i}`} className="h-9 border-b border-gray-50">
                          {Array.from({ length: 14 }).map((_, j) => <td key={j} className="px-3"></td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-2 border-t border-gray-100">
                  <button onClick={addItem} className="flex items-center gap-2 text-blue-700 text-sm font-semibold hover:text-blue-900 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                    Add Row
                  </button>
                </div>
              </div>
            )}

            {/* ── Logistics Tab ── */}
            {activeTab === "logistics" && (
              <div className="p-6 grid grid-cols-2 gap-5">

                {/* Requestor — searchable dropdown */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Requestor</label>
                  <RequestorDropdown
                    value={form.requestor}
                    department={form.department}
                    onSelect={handleSelectRequestor}
                  />
                </div>

                {/* Department — auto-filled, pero pwede pa rin i-edit */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">
                    Department
                    {form.department && (
                      <span className="ml-2 normal-case font-normal text-blue-600 text-[10px]">auto-filled</span>
                    )}
                  </label>
                  <input
                    value={form.department}
                    onChange={e => handleField("department", e.target.value)}
                    placeholder="Auto-filled from requestor"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-blue-50 text-blue-700 font-medium"
                  />
                </div>

                {/* Bill of Lading */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1.5 uppercase tracking-wide">Bill of Lading</label>
                  <input value={form.billOfLading} onChange={e => handleField("billOfLading", e.target.value)}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
            )}

            {/* Accounting Tab */}
            {activeTab === "accounting" && (
              <div className="p-6 text-sm text-gray-400 text-center py-12">Accounting fields — coming soon.</div>
            )}

            {/* Attachments Tab */}
            {activeTab === "attachments" && (
              <div className="p-8 text-center text-gray-400 text-sm">
                <svg className="w-10 h-10 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                No attachments yet.
              </div>
            )}

            {/* Bottom Section */}
            <div className="border-t border-gray-200 px-6 py-4">
              <div className="flex gap-8">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-semibold text-gray-500 w-32 uppercase tracking-wide">Sales Employee</label>
                    <input value={form.salesEmployee} onChange={e => handleField("salesEmployee", e.target.value)}
                      className="border border-gray-300 rounded px-3 py-1.5 text-sm flex-1 max-w-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="text-xs font-semibold text-gray-500 w-32 uppercase tracking-wide">Owner</label>
                    <input value={form.owner} onChange={e => handleField("owner", e.target.value)}
                      className="border border-gray-300 rounded px-3 py-1.5 text-sm flex-1 max-w-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="flex items-start gap-3 mt-2">
                    <label className="text-xs font-semibold text-gray-500 w-32 uppercase tracking-wide mt-2">Remarks</label>
                    <textarea value={form.remarks} onChange={e => handleField("remarks", e.target.value)}
                      rows={3} placeholder="Enter remarks…"
                      className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 max-w-xs focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                  </div>
                </div>

                <div className="min-w-[300px] space-y-2">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Total Before Discount</span>
                    <input value={totalBefore.toFixed(2)} readOnly className="border border-gray-200 rounded px-3 py-1.5 text-sm w-40 bg-gray-50 text-right text-gray-600 cursor-not-allowed" />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Discount</span>
                    <div className="flex items-center gap-1">
                      <input type="number" value={form.discount} onChange={e => handleField("discount", e.target.value)}
                        placeholder="0" className="border border-gray-300 rounded px-2 py-1.5 text-sm w-20 text-right focus:outline-none focus:ring-2 focus:ring-blue-500" />
                      <span className="text-gray-400 text-xs">%</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={form.rounding} onChange={e => handleField("rounding", e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Rounding</span>
                    </div>
                    <input value="PHP 0.00" readOnly className="border border-gray-200 rounded px-3 py-1.5 text-sm w-40 bg-gray-50 text-right text-gray-400 cursor-not-allowed" />
                  </div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Tax</span>
                    <input type="number" value={form.tax} onChange={e => handleField("tax", e.target.value)}
                      placeholder="0" className="border border-gray-300 rounded px-3 py-1.5 text-sm w-40 text-right focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div className="flex items-center justify-between gap-4 border-t border-gray-200 pt-2">
                    <span className="text-sm font-bold text-gray-700 uppercase tracking-wide">Total</span>
                    <input value={`PHP ${total.toFixed(2)}`} readOnly className="border border-gray-300 rounded px-3 py-1.5 text-sm w-40 bg-gray-50 text-right font-bold text-gray-800 cursor-not-allowed" />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={handleSubmit} disabled={loading}
                  className="flex items-center gap-2 bg-yellow-500 text-white px-6 py-2 rounded text-sm font-bold hover:bg-yellow-600 disabled:opacity-60 disabled:cursor-not-allowed transition">
                  {loading ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
                      Adding...
                    </>
                  ) : "Add"}
                </button>
                <button onClick={handleReset} className="px-6 py-2 rounded border border-gray-300 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition">
                  Cancel
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button className="px-4 py-2 rounded border border-gray-300 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition">Copy From</button>
                <button className="px-4 py-2 rounded border border-gray-300 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition">Copy To</button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}